/**
 * Billing API client for Cipher
 *
 * Handles subscription management, checkout sessions, and billing portal.
 */
import apiClient from './client'

export type SubscriptionTier = 'free' | 'informed' | 'strategic' | 'apex_pro'
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'unpaid' | 'trialing'

export interface BillingStatus {
  tier: SubscriptionTier
  status: SubscriptionStatus
  current_period_end: string | null
  cancel_at_period_end: boolean
  stripe_customer_id: string | null
}

export interface CheckoutResponse {
  checkout_url: string
}

export interface PortalResponse {
  portal_url: string
}

export interface TierFeature {
  id: string
  name: string
  price: number
  interval: string
  price_id?: string
  features: string[]
}

export interface PricesResponse {
  tiers: TierFeature[]
}

export interface SubscriptionUpdateResponse {
  success: boolean
  new_tier?: string
  message: string
}

export interface CancelSubscriptionResponse {
  success: boolean
  cancel_at_period_end: boolean
  current_period_end: string
  message: string
}

export const billingApi = {
  /**
   * Get current billing status for the logged-in user.
   */
  getStatus: async (): Promise<BillingStatus> => {
    const response = await apiClient.get('/billing/status')
    return response.data
  },

  /**
   * Create a checkout session for a new subscription or upgrade.
   */
  createCheckoutSession: async (tier: SubscriptionTier): Promise<CheckoutResponse> => {
    const response = await apiClient.post('/billing/create-checkout-session', { tier })
    return response.data
  },

  /**
   * Create a billing portal session for managing subscription.
   */
  createPortalSession: async (): Promise<PortalResponse> => {
    const response = await apiClient.post('/billing/create-portal-session')
    return response.data
  },

  /**
   * Get available subscription tiers and pricing.
   */
  getPrices: async (): Promise<PricesResponse> => {
    const response = await apiClient.get('/billing/prices')
    return response.data
  },

  /**
   * Update (upgrade/downgrade) subscription to a new tier.
   */
  updateSubscription: async (tier: SubscriptionTier): Promise<SubscriptionUpdateResponse> => {
    const response = await apiClient.post('/billing/update-subscription', { tier })
    return response.data
  },

  /**
   * Cancel the current subscription.
   */
  cancelSubscription: async (atPeriodEnd: boolean = true): Promise<CancelSubscriptionResponse> => {
    const response = await apiClient.post('/billing/cancel-subscription', {
      at_period_end: atPeriodEnd
    })
    return response.data
  },

  /**
   * Reactivate a subscription scheduled for cancellation.
   */
  reactivateSubscription: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/billing/reactivate-subscription')
    return response.data
  }
}

/**
 * Helper to redirect user to Stripe checkout.
 */
export const redirectToCheckout = async (tier: SubscriptionTier): Promise<void> => {
  const { checkout_url } = await billingApi.createCheckoutSession(tier)
  window.location.href = checkout_url
}

/**
 * Helper to redirect user to Stripe billing portal.
 */
export const redirectToPortal = async (): Promise<void> => {
  const { portal_url } = await billingApi.createPortalSession()
  window.location.href = portal_url
}

/**
 * Tier display names
 */
export const TIER_NAMES: Record<SubscriptionTier, string> = {
  free: 'Free',
  informed: 'Informed',
  strategic: 'Strategic',
  apex_pro: 'Cipher Pro'
}

/**
 * Tier prices
 */
export const TIER_PRICES: Record<SubscriptionTier, number> = {
  free: 0,
  informed: 9.99,
  strategic: 24.99,
  apex_pro: 59.99
}

/**
 * Tier order for comparison
 */
export const TIER_ORDER: SubscriptionTier[] = ['free', 'informed', 'strategic', 'apex_pro']

/**
 * Check if tier1 is higher than tier2
 */
export const isHigherTier = (tier1: SubscriptionTier, tier2: SubscriptionTier): boolean => {
  return TIER_ORDER.indexOf(tier1) > TIER_ORDER.indexOf(tier2)
}

/**
 * Check if tier1 is lower than tier2
 */
export const isLowerTier = (tier1: SubscriptionTier, tier2: SubscriptionTier): boolean => {
  return TIER_ORDER.indexOf(tier1) < TIER_ORDER.indexOf(tier2)
}

export default billingApi
